/**
 * Returns whether an operator possessing `heldSkillIds` satisfies all `requiredSkillIds` for a job.
 * Condition: requiredSkillIds ⊆ heldSkillIds.
 */
export function isOperatorEligibleForJob(
  heldSkillIds: Iterable<number> | undefined | null,
  requiredSkillIds: Iterable<number> | undefined | null
): boolean {
  if (!requiredSkillIds) return true;
  const held = new Set(heldSkillIds ?? []);
  for (const reqId of requiredSkillIds) {
    if (!held.has(reqId)) {
      return false;
    }
  }
  return true;
}

/**
 * Returns the array of skill IDs that the operator is missing to be eligible for a job.
 */
export function getMissingSkillsForJob(
  heldSkillIds: Iterable<number> | undefined | null,
  requiredSkillIds: Iterable<number> | undefined | null
): number[] {
  if (!requiredSkillIds) return [];
  const held = new Set(heldSkillIds ?? []);
  const missing: number[] = [];
  for (const reqId of requiredSkillIds) {
    if (!held.has(reqId)) {
      missing.push(reqId);
    }
  }
  return missing;
}

/**
 * Convenience helper evaluating an operator object against a job object.
 */
export function checkOperatorSkillEligibility(
  operator: { skills?: Array<{ id: number }> | null } | null | undefined,
  job: { requiredSkills?: Array<{ id: number }> | null } | null | undefined
): { eligible: boolean; missingSkillIds: number[] } {
  const held = (operator?.skills ?? []).map((s) => s.id);
  const required = (job?.requiredSkills ?? []).map((s) => s.id);
  const missingSkillIds = getMissingSkillsForJob(held, required);
  return {
    eligible: missingSkillIds.length === 0,
    missingSkillIds,
  };
}
